import { nanoid } from 'nanoid'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { type IDropOptions, type IInboxTreeCombine, TreeRootInboxes } from 'entities/Inbox'
import { type Inbox } from 'entities/Inbox/model/Inbox'
import { type GroupInbox } from 'entities/Inbox/model/GroupInbox'
import { SmartView } from 'entities/SmartViews/model/SmartView'
import { type ConversationMenuStore } from 'pages/conversations/ui/ConversationMenu'

export const actionMoveToDragDrop = async (
  options: IDropOptions<IInboxTreeCombine>,
  dropRoot: string | number,
  store: ConversationMenuStore
) => {
  const { dropTargetId, dragSourceId, dropTarget, dragSource } = options

  const isChild = (child: string | number | undefined) => {
    if (!child) return false

    const strChild = child.toString()
    const strRoot = dropRoot.toString()

    return strChild === strRoot || strChild.includes(strRoot)
  }

  const realDropTargetId = dropTargetId || dropTarget?.parent
  const dropTargetElement =
    typeof realDropTargetId === 'string' ? store.getItemTree(realDropTargetId) : undefined

  const dropTargetElementId =
    typeof dropTargetElement === 'string' ? dropTargetElement : dropTargetElement?.data.id
  const dragTargetElement =
    typeof dragSourceId === 'string' ? store.getItemTree(dragSourceId) : undefined
  const currentDropTargetId = dragSource?.parent ?? dragTargetElement?.parent
  const dragTarget =
    typeof currentDropTargetId === 'string' ? store.getItemTree(currentDropTargetId) : undefined
  const groupInbox = dragTarget?.data
  const inbox = dragSource?.data ?? dragTargetElement?.data

  if (dragTargetElement?.data instanceof SmartView) return
  if (dragSource?.data instanceof SmartView) return

  const isCrossRootMove =
    (dropRoot === TreeRootInboxes.FAVORITE) !== Boolean(inbox?.is_favorite) && !dropTargetElementId

  if (isCrossRootMove) return

  if (groupInbox?.type === 'inbox_group' && currentDropTargetId !== dropTargetId) {
    const modalId = nanoid()
    modalStore.addModal({
      id: modalId,
      type: ModalTypeList.ALERT,
      title: 'Remove inbox from group?',
      desc: `You're about to remove ${inbox?.name} from the ${groupInbox.name} group inbox. This will affect all members of your organization.`,
      primaryAction: {
        text: 'Remove inbox',
        onAction: async () => {
          if (dropTargetElementId) {
            await store.groupsApiMenuStore.onDeleteTeamsGroupsByIdRemoveTeamById(
              inbox as Inbox,
              groupInbox as GroupInbox
            )
            await store.groupsApiMenuStore.onCreateTeamsGroupsByIdAddTeams(
              inbox as Inbox,
              dropTargetElementId
            )
          } else {
            await store.updateFavorite({
              inbox: inbox as Inbox,
              groupInbox: groupInbox as GroupInbox,
              status: Boolean(inbox?.is_favorite),
            })
          }
          modalStore.closeModal(modalId)
        },
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => {
          modalStore.closeModal(modalId)
        },
      },
    })
    return
  }

  if (!dropTargetElementId) return

  const inboxToMove =
    !isChild(dragSourceId) && dragSource?.data ? dragSource.data : dragTargetElement?.data

  if (!inboxToMove) return

  await store.groupsApiMenuStore.onCreateTeamsGroupsByIdAddTeams(inboxToMove, dropTargetElementId)
}
